import { Dispatch, SetStateAction, useEffect } from 'react';
import { useRelayEnvironment } from 'react-relay';
import { fetchQuery, graphql } from 'relay-runtime';
import { toGlobalID } from 'src/shared/helpers';
import { InputImageData } from '../types';
import { ProductSideImage } from '../types';
import { getSideImageAsBase64 } from '../utils/imageHelpers';
import type { useIframeMessagesSceneQuery } from './__generated__/useIframeMessagesSceneQuery.graphql';
import { AIStudioState, updateToolState } from './useAIStudioState';

const SceneQuery = graphql`
    query useIframeMessagesSceneQuery($productId: ID!) {
        model(id: $productId) {
            scenes {
                sceneID
            }
        }
    }
`;

interface UseIframeMessagesParams {
    updateProduct: (updates: Record<string, unknown>) => void;
    setIsIframeMode: Dispatch<SetStateAction<boolean>>;
    setState: Dispatch<SetStateAction<AIStudioState>>;
    setSelectedImageIndex: Dispatch<SetStateAction<number>>;
    setViewMode: Dispatch<SetStateAction<'images' | '3d'>>;
    setIsProductInputEditing: Dispatch<SetStateAction<boolean>>;
    setInputImages: Dispatch<SetStateAction<InputImageData[]>>;
    setIsAutoCapturing: Dispatch<SetStateAction<boolean>>;
}

/**
 * Listens for OPEN_AI_STUDIO postMessage from the customizer iframe
 * and updates product context + UI state accordingly.
 */
export function useIframeMessages({
    updateProduct,
    setIsIframeMode,
    setState,
    setSelectedImageIndex,
    setViewMode,
    setIsProductInputEditing,
    setInputImages,
    setIsAutoCapturing
}: UseIframeMessagesParams): void {
    const relayEnvironment = useRelayEnvironment();

    useEffect(() => {
        const handleOpenAIStudio = (event: MessageEvent) => {
            if (event.data?.type === 'OPEN_AI_STUDIO') {
                const aiProductId = parseInt(event.data?.params?.productId, 10);
                const variantId = event.data?.params?.variantId ? parseInt(event.data?.params?.variantId, 10) : null;
                const docId = event.data?.params?.docId as string | undefined;
                const compositionDocId = event.data?.params?.compositionDocId as string | undefined;
                const selectedSideId = event.data?.params?.selectedSideId as number | undefined;
                const sidesData = event.data?.params?.sides as
                    | Array<{
                          sideId: number;
                          sideName?: string;
                          preview: string;
                      }>
                    | undefined;

                let sideImagesFromMsg: ProductSideImage[] | undefined;
                if (sidesData && sidesData.length > 0) {
                    sideImagesFromMsg = sidesData.map((side) => ({
                        sideId: side.sideId,
                        sideName: side.sideName,
                        url: side.preview // base64 data URL
                    }));
                }

                if (aiProductId) {
                    // Remove only product-sourced images, keep user-uploaded ones
                    setInputImages((prev) => prev.filter((img) => !img.fromProduct));

                    // Common setup: update product, exit iframe, set editor view
                    const setupCommon = (productUpdates: Record<string, unknown>, initialIndex?: number) => {
                        updateProduct(productUpdates);
                        setIsIframeMode(false);
                        setIsProductInputEditing(false);
                        setState((prev) => {
                            if (!prev.selectedTool) return prev;
                            return updateToolState(prev, prev.selectedTool, { view: 'editor' });
                        });
                        if (initialIndex != null) {
                            setSelectedImageIndex(initialIndex);
                        }
                    };

                    // Check if product has a 3D scene via GraphQL
                    const globalId = toGlobalID(aiProductId, 'Model');
                    fetchQuery<useIframeMessagesSceneQuery>(relayEnvironment, SceneQuery, { productId: globalId })
                        .toPromise()
                        .then((data) => {
                            const has3DScene = !!(data?.model?.scenes && data.model.scenes.length > 0 && data.model.scenes[0].sceneID);
                            let initialIndex = 0;

                            if (sideImagesFromMsg) {
                                if (selectedSideId != null) {
                                    const matchedIndex = sideImagesFromMsg.findIndex((s) => s.sideId === selectedSideId);
                                    if (matchedIndex >= 0) {
                                        initialIndex = matchedIndex;
                                    }
                                }
                            }

                            setupCommon(
                                {
                                    variantId,
                                    sideImages: sideImagesFromMsg,
                                    docId,
                                    compositionDocId
                                },
                                initialIndex
                            );

                            if (has3DScene) {
                                // Product has 3D — prefer 3D view and auto-capture
                                setViewMode('3d');
                                setIsAutoCapturing(true);
                            } else if (sideImagesFromMsg) {
                                // No 3D — auto-create input image from the selected side
                                const selectedSide = sideImagesFromMsg[initialIndex];
                                if (selectedSide) {
                                    getSideImageAsBase64(selectedSide).then((base64) => {
                                        if (base64) {
                                            const productImage: InputImageData = {
                                                id: `input-side-${Date.now()}`,
                                                url: base64,
                                                name: selectedSide.sideName || 'Product image',
                                                fromProduct: true
                                            };
                                            setInputImages((prev) => [...prev.filter((img) => !img.fromProduct), productImage]);
                                        }
                                    });
                                }
                            }
                        })
                        .catch((err) => {
                            console.error('Failed to check product scene:', err);
                        });
                }
            }
        };

        window.addEventListener('message', handleOpenAIStudio);
        return () => window.removeEventListener('message', handleOpenAIStudio);
    }, [updateProduct, setIsIframeMode, setState, setSelectedImageIndex, setViewMode, setInputImages, setIsAutoCapturing, relayEnvironment]);
}
